import { useMemo } from 'react';
import lowerCase from 'lodash/lowerCase';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { dateFormatTZ } from '@mgm/common/utils';
import { useBaseFilter } from '../../../../hooks/useFilter';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedFilterItems } from '../../../../store/common/selectors';
import { getScheduleList, getTimeOffs } from '../../../../store/schedules/selectors';
import { TScheduleShift, TTimeOff } from '../../../../types';
import { typeColorMap } from '../data/calendar';

export function useCalendarFilter() {
  const { baseFilters } = useBaseFilter();
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const { t } = useTranslation('schedule');

  const scheduleList = useAppSelector(getScheduleList);
  const timeOffs = useAppSelector(getTimeOffs);

  const formattedTimeOffs = useMemo(() => timeOffs.map((timeoff) => ({
    ...timeoff,
    textColor: typeColorMap[timeoff.type],
  })), [timeOffs]);

  const calendarShifts = useMemo(() => Object.values(scheduleList).reduce((acc, scheduleData) => {
    if (scheduleData.schedules.length) {
      const formattedShifts = scheduleData.schedules?.map((shift) => ({
        ...shift,
        title: `${dateFormatTZ(shift.start, 'h:mm A', shift.timezone)} - ${dateFormatTZ(shift.end, 'h:mm A', shift.timezone)}`,
        textColor: 'rgb(142, 51, 255)',
      }));

      acc.push(...formattedShifts);
    }
    return acc;
  }, [] as Array<TScheduleShift>), [scheduleList]);

  const filterByDepartments = (
    items: Array<TScheduleShift>,
  ) => {
    const departments = selectedFilterItems.department.map(
      (department) => lowerCase(department.name),
    );

    return items.filter((item) => {
      const department = lowerCase(item.department);

      return departments.includes(department);
    });
  };

  const filterByPositions = (
    items: Array<TScheduleShift>,
  ) => {
    const positions = selectedFilterItems.position.map(
      (department) => lowerCase(department.name),
    );

    return items.filter((item) => {
      const position = lowerCase(item.position);

      return positions.includes(position);
    });
  };

  const filterByLocations = (
    items: Array<TScheduleShift>,
  ) => {
    const localLocations = selectedFilterItems.location.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localLocations.includes(item.location?.name?.toLowerCase()));
  };

  const filterByTypes = (
    items: Array<TTimeOff>,
  ) => {
    const localTypes = selectedFilterItems.timeoffTypes.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localTypes.includes(item.type?.toLowerCase()));
  };

  const filterByStatuses = (
    items: Array<TTimeOff>,
  ) => {
    const localStatuses = selectedFilterItems.timeoffStatuses.map(
      (item) => item.name?.toLowerCase(),
    );

    return items.filter((item) => localStatuses.includes(item.status?.toLowerCase()));
  };

  const getCalendarByFilters = useMemo(() => {
    let filteredMembers = calendarShifts;

    if (selectedFilterItems.department?.length) {
      filteredMembers = filterByDepartments(calendarShifts);
    }

    if (selectedFilterItems.position?.length) {
      filteredMembers = filterByPositions(calendarShifts);
    }

    if (selectedFilterItems.location?.length) {
      filteredMembers = filterByLocations(calendarShifts);
    }

    return filteredMembers;
  }, [calendarShifts, selectedFilterItems]);

  const getTimeoffByFilter = useMemo(() => {
    let filteredMembers = formattedTimeOffs;

    if (selectedFilterItems.timeoffTypes?.length) {
      filteredMembers = filterByTypes(formattedTimeOffs);
    }

    if (selectedFilterItems.timeoffStatuses?.length) {
      filteredMembers = filterByStatuses(formattedTimeOffs);
    }
    return filteredMembers;
  }, [formattedTimeOffs, selectedFilterItems]);

  const calendarFilter = useMemo(() => {
    const uniqueTypes = formattedTimeOffs.reduce((acc: Array<{ name: string, value: string }>, item) => {
      const { type } = item;

      if (type) {
        const existingType = acc.find((entry) => entry.name === type);
        if (!existingType) {
          acc.push({ name: startCase(type), value: type });
        }
      }

      return acc;
    }, []);

    const uniqueStatuses = formattedTimeOffs.reduce((acc: Array<{ name: string, value: string }>, item) => {
      const { status } = item;

      if (status) {
        const existingType = acc.find((entry) => entry.name === status);
        if (!existingType) {
          acc.push({ name: startCase(status), value: status });
        }
      }

      return acc;
    }, []);

    return [
      {
        value: 'timeoffTypes',
        selectedKey: 'timeoffTypes',
        name: t('timeoffTypes'),
        children: uniqueTypes,
        isSelected: false,
      },
      {
        value: 'timeoffStatuses',
        selectedKey: 'timeoffStatuses',
        name: t('timeoffStatuses'),
        children: uniqueStatuses,
        isSelected: false,
      },
    ];
  }, [formattedTimeOffs]);

  return {
    calendarFilterItems: [...baseFilters, ...calendarFilter],
    calendarShifts: [...getCalendarByFilters, ...getTimeoffByFilter],
  };
}
